import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Flex, Checkbox } from "antd"
import SignatureCanvas from "react-signature-canvas"
import styles from "./index.module.css"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { updateWaiverSubmission, getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"

export const SignDocumentModal = ({
  uuid,
  status,
  waiverSubmissionId,
  pendingSubmission,
  waiver,
  open,
  onCancel,
  onNext,
  bookingWaivers
}) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const { t } = useTranslation()
  const { loading: submitting } = useSelector((state) => state.digitalWaiver)
  const [isChecked, setIsChecked] = useState(false)
  const [fileList, setFileList] = useState([])
  const [pdfUrls, setPdfUrls] = useState([])
  console.log(pdfUrls, "pdfUrlspdfUrls")
  const [responseId, setResponseId] = useState(null)
  // console.log(responseId,'responseIdresponseId')
  const [currentWaiverIndex, setCurrentWaiverIndex] = useState(0)
  const [signatureDataUrl, setSignatureDataUrl] = useState(null)
  const [documentStates, setDocumentStates] = useState([]) // Store checkbox and signature for each document
  const [signatureDisabled, setSignatureDisabled] = useState(false) // Control signature canvas interaction
  const [isNavigating, setIsNavigating] = useState(false) // Track when user is navigating between documents
  const signCanvas = useRef(null)
  const handleSignatureEnd = () => {
    // Prevent signature if document is submitted
    if (signatureDisabled) return

    const pad = signCanvas.current
    if (!pad || typeof pad.isEmpty !== "function" || pad.isEmpty() || typeof pad.getTrimmedCanvas !== "function") {
      setSignatureDataUrl(null)
      // Update document states
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            signatureDataUrl: null
          }
        }
        return newStates
      })
      return
    }
    const dataUrl = pad.toDataURL()
    setSignatureDataUrl(dataUrl)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: dataUrl
        }
      }
      return newStates
    })
  }

  useEffect(() => {
    const sharedWaiver = status === "partially_signed" ? waiver?.pending_submissions : waiver?.waivers
    const tripBookingWaivers = pendingSubmission?.length > 0 ? pendingSubmission : bookingWaivers
    const waivers = sharedWaiver || tripBookingWaivers || []
    if (waivers.length > 0) {
      const now = new Date()
      const initialFileObjects = waivers.map((waiverItem, index) => {
        const fileName = waiverItem?.document_filename || waiverItem?.document_url?.split("/").pop() || "document.pdf"
        return {
          uid: `existing-${Date.now()}-${index}`,
          lastModified: now.getTime(),
          lastModifiedDate: now.toISOString(),
          name: fileName,
          type: "application/pdf",
          percent: 0,
          status: "uploading",
          url: waiverItem.document_url
        }
      })

      setFileList(initialFileObjects)

      // Load all documents
      const fetchDocuments = async () => {
        try {
          const filePromises = waivers.map(async (waiverItem, index) => {
            const fileName = waiverItem.document_filename || waiverItem.document_url.split("/").pop() || "document.pdf"

            const response = await fetch(waiverItem.document_url)
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Get the blob and create a file object
            const blob = await response.blob()
            const file = new File([blob], fileName, {
              type: blob.type || "application/pdf",
              lastModified: now.getTime()
            })

            // Create the final file object with all properties
            return {
              ...initialFileObjects[index],
              size: blob.size,
              status: "done",
              originFileObj: file
            }
          })

          const fileObjects = await Promise.all(filePromises)
          setFileList(fileObjects)

          // Set PDF URLs for all documents
          const pdfObjectUrls = fileObjects.map((fileObj) => URL.createObjectURL(fileObj.originFileObj))
          setPdfUrls(pdfObjectUrls)

          // Clean up function to revoke object URLs on unmount
          return () => {
            pdfObjectUrls.forEach((url) => URL.revokeObjectURL(url))
          }
        } catch (error) {
          console.error("Error fetching documents:", error)
          // Update file list with error state
          setFileList(
            initialFileObjects.map((obj) => ({
              ...obj,
              status: "error",
              error: "Failed to load document"
            }))
          )
        }
      }
      fetchDocuments()
    }
  }, [waiver?.waivers, bookingWaivers])

  // Initialize document states when waivers are loaded
  useEffect(() => {
    if (pdfUrls.length > 0) {
      const newStates = Array(pdfUrls.length)
        .fill(null)
        .map(() => ({
          isChecked: false,
          signatureDataUrl: null,
          submitted: false
        }))
      setDocumentStates(newStates)
    }
  }, [pdfUrls.length])

  // Update current checkbox and signature when index changes
  useEffect(() => {
    if (documentStates[currentWaiverIndex]) {
      setIsChecked(documentStates[currentWaiverIndex].isChecked)
      
      // Only restore signature if document is submitted
      if (documentStates[currentWaiverIndex]?.submitted) {
        setSignatureDataUrl(documentStates[currentWaiverIndex].signatureDataUrl)
      } else if (isNavigating) {
        // Clear signature only when navigating to unsigned documents
        setSignatureDataUrl(null)
        setIsNavigating(false) // Reset navigation flag
      }

      // Only disable signature if document is already submitted
      const shouldDisable = documentStates[currentWaiverIndex]?.submitted
      setSignatureDisabled(shouldDisable)
    }
  }, [currentWaiverIndex, documentStates])

  // Restore signature on canvas when signatureDataUrl changes
  useEffect(() => {
    if (signCanvas.current) {
      const canvas = signCanvas.current
      // Clear canvas first to prevent overlapping signatures
      if (typeof canvas.clear === "function") {
        canvas.clear()
      }
      // Only restore if there's signature data
      if (signatureDataUrl && typeof canvas.fromDataURL === "function") {
        canvas.fromDataURL(signatureDataUrl)
      }

      // Apply disabled state to canvas element
      const canvasElement = canvas.getCanvas ? canvas.getCanvas() : null
      if (canvasElement) {
        if (signatureDisabled) {
          canvasElement.style.pointerEvents = "none"
          canvasElement.style.opacity = "0.5"
          canvasElement.style.cursor = "not-allowed"
        } else {
          canvasElement.style.pointerEvents = "auto"
          canvasElement.style.opacity = "1"
          canvasElement.style.cursor = "crosshair"
        }
      }
    }
  }, [signatureDataUrl, signatureDisabled])

  const clearSignature = () => {
    if (signCanvas.current && typeof signCanvas.current.clear === "function") {
      signCanvas.current.clear()
    }
    setSignatureDataUrl(null)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: null
        }
      }
      return newStates
    })
  }
  const handleSubmit = async () => {
    // Check if current document has already been submitted
    if (documentStates[currentWaiverIndex]?.submitted) {
      // Already submitted, just move to next document
      if (currentWaiverIndex < pdfUrls.length - 1) {
        const nextIndex = currentWaiverIndex + 1
        setCurrentWaiverIndex(nextIndex)
        // Check if next document is submitted to determine if signature should be disabled
        if (documentStates[nextIndex]?.submitted) {
          setSignatureDisabled(true)
        } else {
          setSignatureDisabled(false)
          setIsNavigating(true) // Set navigation flag
          // Clear signature when moving to unsigned next document
          setSignatureDataUrl(null)
        }
      } else {
        // This was the last document, submission is complete
        if (onNext) {
          onNext()
        }
      }
      return
    }

    const formData = new FormData()

    // Always send the current document's state
    const sharedWaiver = status === "partially_signed" ? waiver?.pending_submissions : waiver?.waivers
    const tripBookingWaivers = pendingSubmission?.length > 0 ? pendingSubmission : bookingWaivers

    if (sharedWaiver || bookingWaivers) {
      const waivers = sharedWaiver || tripBookingWaivers || []
      const currentWaiverItem = waivers[currentWaiverIndex]
      console.log(currentWaiverItem, "currentWaiverItemcurrentWaiverItem")
      if (currentWaiverItem) {
        formData.append(
          `waiver_submission[waiver_submission_documents_attributes][0][booking_waiver_id]`,
          currentWaiverItem.booking_waiver_id
        )

        // Add current document's signature if available
        if (documentStates[currentWaiverIndex]?.signatureDataUrl) {
          formData.append(
            `waiver_submission[waiver_submission_documents_attributes][0][signature]`,
            documentStates[currentWaiverIndex].signatureDataUrl
          )
        }
      }
    }

    // Send API call for current document
    await dispatch(updateWaiverSubmission(waiverSubmissionId, formData)).then((response) => {
      setResponseId(response?.data?.id || null)
      toaster.success(response?.message)

      // Mark current document as submitted
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            submitted: true
          }
        }
        return newStates
      })
    })

    // Check if there are more documents to show
    if (currentWaiverIndex < pdfUrls.length - 1) {
      // Move to next document
      const nextIndex = currentWaiverIndex + 1
      setCurrentWaiverIndex(nextIndex)
      // Check if next document is submitted to determine if signature should be disabled
      if (documentStates[nextIndex]?.submitted) {
        setSignatureDisabled(true)
      } else {
        setSignatureDisabled(false)
        setIsNavigating(true) // Set navigation flag
        // Clear signature when moving to unsigned next document
        setSignatureDataUrl(null)
      }
    } else {
      // This was the last document, submission is complete - call getWaiverSubmissionsByTripBooking
      dispatch(getWaiverSubmissionsByTripBooking(uuid))

      // Close the modal
      if (onCancel) {
        onCancel()
      }
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={800}
      maskClosable={false}
      title={
        <span className={styles.modalTitle} style={{ fontWeight: 400, color: "#666", fontSize: "16px" }}>
          Sign the document.
        </span>
      }
    >
      <>
        {/* Document Preview Section */}
        <div className={styles.previewSection}>
          <div className={styles.title}>Document Preview</div>
          {pdfUrls.length > 0 && (
            <div className={styles.pdfPreviewContainer}>
              <div className={styles.pdfViewer}>
                <Flex align="center" justify="end">
                  {currentWaiverIndex + 1} of {waiver?.waivers?.length || bookingWaivers?.length || 0} waivers
                  {/* {currentWaiverIndex + 1} of {pdfUrls.length} waivers */}
                </Flex>
                <div className={`mb-xs ${styles.documentTitle}`}>
                  Document {currentWaiverIndex + 1}: {fileList[currentWaiverIndex]?.name || `Document ${currentWaiverIndex + 1}`}
                </div>
                <object data={pdfUrls[currentWaiverIndex]} type="application/pdf" className={styles.pdfObject}>
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    Unable to display PDF preview. Please download the file to view it.
                  </div>
                </object>
              </div>
            </div>
          )}

          {/* Legal Text & Checkbox */}
          <div className={`mt-xs ${styles.legalSection}`}>
            <Flex gap={12} align="start">
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  const newChecked = e.target.checked
                  setIsChecked(newChecked)
                  // Update document states
                  setDocumentStates((prev) => {
                    const newStates = [...prev]
                    if (newStates[currentWaiverIndex]) {
                      newStates[currentWaiverIndex] = {
                        ...newStates[currentWaiverIndex],
                        isChecked: newChecked
                      }
                    }
                    return newStates
                  })
                }}
                className={`mt-xs ${styles.checkboxTop}`}
              />
              <div className={styles.legalText}>
                <div
                  onClick={() => {
                    const newChecked = !isChecked
                    setIsChecked(newChecked)
                    // Update document states
                    setDocumentStates((prev) => {
                      const newStates = [...prev]
                      if (newStates[currentWaiverIndex]) {
                        newStates[currentWaiverIndex] = {
                          ...newStates[currentWaiverIndex],
                          isChecked: newChecked
                        }
                      }
                      return newStates
                    })
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <p>{t("digital_waiver_and_contracts.leagal_documents")}</p>
                </div>
                <p>{t("digital_waiver_and_contracts.legal_document_desc")}</p>
              </div>
            </Flex>

            {/* Signature Section - Custom Styled */}
            {isChecked && (
              <div className={`mt-m relative ${styles.canvasLayer}`}>
                <div className={styles.title}>{t("digital_waiver_and_contracts.signature")}</div>
                <div className={`${styles.signatureCanvasWrapper} ${signatureDisabled ? styles.disabledCanvas : ""}`}>
                  {/* Actual Canvas Layer */}
                  <SignatureCanvas ref={signCanvas} penColor="black" onEnd={handleSignatureEnd} />
                  <div className={styles.signaturePlaceholderText}>{t("digital_waiver_and_contracts.draw_signature")}</div>
                  {/* Disabled overlay */}
                  {signatureDisabled && <div className={styles.disabledOverlay} />}
                </div>
                <Button
                  type="text"
                  size="small"
                  onClick={clearSignature}
                  className={styles.clearButton}
                  icon={<span style={{ fontSize: "20px" }}>×</span>}
                  disabled={signatureDisabled}
                ></Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8} className={styles.poweredBy}>
            {t("settings.powered_by")} <img className={`ml-xxs ${styles.logoImage}`} src={logoSrc} alt="Powered by" />
          </Flex>

          <Flex gap={12}>
            {waiver?.pending_submissions?.length === 1 || waiver?.waivers?.length === 1 ? null : (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  if (currentWaiverIndex > 0) {
                    const prevIndex = currentWaiverIndex - 1
                    setIsNavigating(true) // Set navigation flag
                    setCurrentWaiverIndex(prevIndex)
                  }
                }}
                disabled={currentWaiverIndex === 0}
              >
                {t("embedded.booking_confirmation.previous")}
              </Button>
            )}

            <Button
              color="primary"
              variant="solid"
              loading={submitting}
              onClick={handleSubmit}
              disabled={!isChecked || !signatureDataUrl || submitting}
            >
              {currentWaiverIndex < pdfUrls.length - 1 ? t("embedded.booking_confirmation.next") : "Submit"}
            </Button>
          </Flex>
        </Flex>
      </>
    </Modal>
  )
}

SignDocumentModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  initialData: PropTypes.object,
  status: PropTypes.string
}
